/**
 * SERP URLs that are poor SEO Quick Compare / rival-crawl targets: brand
 * homepages, login/auth walls, and thin app-shell create/new URLs.
 *
 * Heuristic from URL path (+ optional title/snippet) — no domain hardcoding.
 * Keep in sync with backend/src/utils/weak-serp-compare-target.ts
 */

export type WeakSerpCompareReason = 'homepage' | 'login' | 'app_shell';

const LOGIN_PATH_SEGMENTS = new Set([
  'login',
  'log-in',
  'signin',
  'sign-in',
  'signup',
  'sign-up',
  'register',
  'auth',
  'oauth',
  'sso',
  'weblogin'
]);

/** Final path segment typical of SaaS create / editor shells (not article slugs). */
const APP_SHELL_TAIL_SEGMENTS = new Set([
  'create',
  'new',
  'start',
  'compose',
  'editor',
  'dashboard',
  'app'
]);

const LOGIN_TEXT_RE = /\b(sign[\s-]?in|log[\s-]?in|log[\s-]?on|create\s+an?\s+account)\b/i;

function pathSegmentsFromUrl(url: string): string[] | null {
  try {
    const pathname = new URL(url).pathname || '/';
    return pathname
      .split('/')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .map((s) => s.replace(/\.(html?|php|aspx?)$/i, ''));
  } catch {
    return null;
  }
}

function isLoginPath(segments: string[]): boolean {
  return segments.some(
    (s) =>
      LOGIN_PATH_SEGMENTS.has(s) ||
      s.includes('login') ||
      s.startsWith('oauth') ||
      s === 'accounts'
  );
}

function isAppShellPath(segments: string[]): boolean {
  if (segments.length === 0 || segments.length > 3) return false;
  const last = segments[segments.length - 1];
  // Article slugs are usually hyphenated ("how-to-create-slides"); shell tails are bare.
  if (last.includes('-') || last.includes('_')) return false;
  return APP_SHELL_TAIL_SEGMENTS.has(last);
}

function isLoginCopy(title?: string, description?: string): boolean {
  const blob = `${title || ''} ${description || ''}`.trim();
  if (!blob) return false;
  return LOGIN_TEXT_RE.test(blob);
}

/**
 * Why this SERP URL is a weak compare target, or null if it looks editorial.
 */
export function weakSerpCompareReason(
  url: string,
  opts?: { title?: string; description?: string }
): WeakSerpCompareReason | null {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;

  const segments = pathSegmentsFromUrl(trimmed);
  if (!segments) return null;

  if (segments.length === 0) return 'homepage';
  if (isLoginPath(segments) || isLoginCopy(opts?.title, opts?.description)) return 'login';
  if (isAppShellPath(segments)) return 'app_shell';
  return null;
}

export function isWeakSerpCompareTarget(
  url: string,
  opts?: { title?: string; description?: string }
): boolean {
  return weakSerpCompareReason(url, opts) !== null;
}

/** Short label for UI badges (Social-style). */
export function weakSerpCompareBadgeLabel(reason: WeakSerpCompareReason): string {
  switch (reason) {
    case 'homepage':
      return 'Homepage';
    case 'login':
      return 'Login';
    case 'app_shell':
      return 'App';
  }
}

/** Human note for playbook when earlier rivals were skipped. */
export function formatWeakSerpSkipNote(reasons: WeakSerpCompareReason[]): string | null {
  if (!reasons.length) return null;
  const unique = [...new Set(reasons)];
  const parts = unique.map((r) => {
    switch (r) {
      case 'homepage':
        return 'homepage';
      case 'login':
        return 'login';
      case 'app_shell':
        return 'app-shell';
    }
  });
  if (parts.length === 1) return `Skipped ${parts[0]} result(s) above`;
  if (parts.length === 2) return `Skipped ${parts[0]}/${parts[1]} result(s) above`;
  return `Skipped non-editorial result(s) above`;
}
